/**
 * useServiceWorkerUpdate
 *
 * Detects when a new service worker version is waiting to activate and
 * surfaces { needsRefresh: true } so the caller can show an update banner.
 *
 * WHY THIS IS NEEDED
 * ------------------
 * The app's VitePWA config uses injectManifest mode with registerType
 * "autoUpdate". The generated registerSW.js only registers the SW — it does
 * NOT post SKIP_WAITING or reload after controllerchange. Without this hook,
 * users on installed PWAs run stale JS indefinitely after a deploy.
 *
 * HOW IT WORKS
 * ------------
 * 1. Waits for the SW registration to be ready.
 * 2. Checks for a waiting worker immediately (handles the "user refreshed
 *    the page while a new SW was already waiting" case).
 * 3. Listens for `updatefound` on the registration; when the new SW reaches
 *    the `installed` state, sets needsRefresh = true.
 * 4. Also listens for the `__sw_update_available__` window event, which allows
 *    E2E tests to trigger the banner without a real SW registration.
 *
 * When the user calls applyUpdate():
 * - Posts SKIP_WAITING to the waiting SW (activates it immediately).
 * - Listens for `controllerchange` and reloads (new SW now serves new assets).
 * - Falls back to a reload after 2 s in case controllerchange never fires.
 */
import { useState, useEffect, useRef, useCallback } from "react";

interface Options {
  reload?: () => void;
}

export function useServiceWorkerUpdate({ reload = () => window.location.reload() }: Options = {}): {
  needsRefresh: boolean;
  applyUpdate: () => void;
} {
  const [needsRefresh, setNeedsRefresh] = useState(false);
  const waitingWorkerRef = useRef<ServiceWorker | null>(null);
  const reloadRef = useRef(reload);
  reloadRef.current = reload;

  useEffect(() => {
    function markReady(worker: ServiceWorker): void {
      waitingWorkerRef.current = worker;
      setNeedsRefresh(true);
    }

    // Real SW path
    let registration: ServiceWorkerRegistration | null = null;

    function onUpdateFound(): void {
      const newWorker = registration?.installing;
      if (!newWorker) return;
      newWorker.addEventListener("statechange", () => {
        if (
          newWorker.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          markReady(newWorker);
        }
      });
    }

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready
        .then((reg) => {
          registration = reg;
          if (reg.waiting && navigator.serviceWorker.controller) {
            markReady(reg.waiting);
          }
          reg.addEventListener("updatefound", onUpdateFound);
        })
        .catch(() => {});
    }

    // E2E / dev testability path
    function onTestEvent(): void {
      const waiting = (window as any).__sw_waiting_worker__ ?? null;
      waitingWorkerRef.current = waiting;
      setNeedsRefresh(true);
    }
    window.addEventListener("__sw_update_available__", onTestEvent);

    return () => {
      window.removeEventListener("__sw_update_available__", onTestEvent);
      if (registration) {
        registration.removeEventListener("updatefound", onUpdateFound);
      }
    };
  }, []);

  const applyUpdate = useCallback(() => {
    if (waitingWorkerRef.current) {
      waitingWorkerRef.current.postMessage({ type: "SKIP_WAITING" });
    }

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        () => reloadRef.current(),
        { once: true },
      );
    }

    // Safety fallback: reload after 2 s if controllerchange never fires.
    setTimeout(() => reloadRef.current(), 2000);
  }, []);

  return { needsRefresh, applyUpdate };
}
